import React from "react";
import { push } from "store/actions/router";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { toNumber, round } from "lodash";

import StateTax from "components/StateTax";
import type { RootState } from "types/state";

interface StateTaxData {
  stateTaxWithholding: string;
  stateTaxWithholdingPercentage: string;
  stateTaxWithholdingAmount: string;
}

interface OwnProps {
  // react-step-wizard injects goToNamedStep at runtime (the workflows omit it);
  // optional in the contract, non-null-asserted at call sites (house pattern).
  goToNamedStep?: (step: string) => void;
  onNext?: () => void;
  withdrawalAmount?: number;
  userState?: string;
  stateTaxWithholdingChange?: (data: StateTaxData) => void;
  onStateTaxSelection: (data: StateTaxData) => void;
  isRothDistribution?: boolean;
  accountValue?: number;
  isTotalDisbursement?: boolean;
  federalTaxSelection?: { percentage?: number | string };
  isAccountClosure?: boolean;
  // react-step-wizard injects stepName at runtime; the workflows declare it
  // on the JSX element.
  stepName?: string;
}

const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
  const { id, profile: userInfo } = state.user;

  return {
    id,
    userInfo,
    ownProps,
  };
};

const mapDispatchToProps = {
  push,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  stateTaxWithholding: string | null;
  stateTaxWithholdingPercentage: string | null;
  stateTaxWithholdingAmount: string | null;
}

export class DistributionStateTax extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      stateTaxWithholding: null,
      stateTaxWithholdingPercentage: null,
      stateTaxWithholdingAmount: null,
    };
  }

  handleStateTaxChange = (stateTaxData: StateTaxData) => {
    this.setState(stateTaxData);
    if (this.props.stateTaxWithholdingChange) {
      this.props.stateTaxWithholdingChange(stateTaxData);
    }

    this.props.onStateTaxSelection(stateTaxData);
  };

  handleCancel = () => {
    this.props.goToNamedStep!("distributionFederalTax");
  };

  goToNextStep = () => {
    this.props.goToNamedStep!("distributionSummary");
  };

  calculateFederalTax = () => {
    const { withdrawalAmount, federalTaxSelection } = this.props;
    // federalTaxSelection is optional; preserve original access (throws when absent).
    const { percentage } = federalTaxSelection!;

    const percentageNumber = toNumber(percentage);

    if (!(percentageNumber > 0)) {
      return 0;
    }

    // withdrawalAmount is optional; preserve original arithmetic.
    return round((percentageNumber / 100) * (withdrawalAmount as number), 2);
  };

  render() {
    return (
      <StateTax
        accountValue={this.props.accountValue}
        federalWithholdingAmount={this.calculateFederalTax()}
        isTotalDisbursement={this.props.isTotalDisbursement}
        isRothDistribution={this.props.isRothDistribution}
        federalTaxSelection={this.props.federalTaxSelection}
        // StateTax manages these internally; the original passed them as inert
        // extra props. A single @ts-expect-error covers the excess-property
        // error TS reports for the first unknown prop on this element; the
        // remaining two are flagged together, so no further directives are
        // needed (they would be "unused").
        // @ts-expect-error preserve inert extra props not in StateTax's props
        stateTaxWithholding={this.state.stateTaxWithholding}
        stateTaxWithholdingPercentage={this.state.stateTaxWithholdingPercentage}
        stateTaxWithholdingAmount={this.state.stateTaxWithholdingAmount}
        stateTaxWithholdingChange={this.handleStateTaxChange}
        withdrawalAmount={this.props.withdrawalAmount}
        userState={this.props.userInfo.state as string}
        goToNamedStep={this.props.goToNamedStep}
        onCancel={this.handleCancel}
        onNext={this.goToNextStep}
      />
    );
  }
}

export default connector(withApollo<Props>(DistributionStateTax));
